import React from 'react';
import { OverlayView } from "@react-google-maps/api";
import { ArcBatteryLife, ArcButton, ArcIconButton, ArcStack, ArcText } from "@src/core/ui";
import { ReactComponent as CloseIcon } from '@src/assets/icons/close-black.svg';
import { EMPTY_STRING } from '@src/core/config';
import { DroneStatusType, getDroneStatusDisplay } from '@src/core/features';
import { useFlightProvider } from '@src/features/order/provider';
import { DroneInfoOverlayProps } from "./types";
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { setHoveredDrone } from '@src/features/drone/redux';
import { selectDroneOnMap } from '@src/features/drone/utils/select-drone-on-map';

export const DroneInfoOverlay: React.FC<DroneInfoOverlayProps> = (props) => {
  const { drone, position, onClose, clickedDrone, setClickedDrone } = props;
  const { flightData } = useFlightProvider();
  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();
  const onSelect = React.useCallback(() => {
    if (!clickedDrone) return;
    setClickedDrone(null);
    dispatch(setHoveredDrone(null));
    selectDroneOnMap(dispatch, setSearchParams, clickedDrone);
  }, [clickedDrone, dispatch, setSearchParams]);

  const isOnMission = drone.status === DroneStatusType.MISSION;
  const isFlightForThisDrone = isOnMission && flightData?.serialNumber === drone.serialNumber;
  const flightLat = isFlightForThisDrone ? Number(flightData?.latitude) : NaN;
  const flightLng = isFlightForThisDrone ? Number(flightData?.longitude) : NaN;
  const useFlightCoords = !isNaN(flightLat) && !isNaN(flightLng);

  // Update position if drone is on mission and moving
  const currentPosition = React.useMemo(() => {
    if (useFlightCoords) {
      return { lat: flightLat, lng: flightLng };
    }
    return position;
  }, [useFlightCoords, flightLat, flightLng, position]);

  return (
    <OverlayView
      position={currentPosition}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <ArcStack
        style={{
          position: "relative",
          background: "#fff",
          padding: "12px 16px",
          borderRadius: "8px",
          border: "1px solid rgba(0,0,0,0.2)",
          boxShadow: "0px 2px 7px rgba(0, 0, 0, 0.3)",
          minWidth: "280px",
          transform: "translate(-50%, -130%)",
          zIndex: 1000,
        }}
      >
        <ArcIconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: "5px",
            right: "8px",
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </ArcIconButton>
        <ArcStack spacing={2} marginTop={3}>
          <ArcStack 
            spacing={1} 
          >
            <ArcStack 
              spacing={1} 
              direction='row' 
              alignItems='center'
              justifyContent='space-between'>
              <ArcText.h6 maxLines={1} color='black'> {drone.name ?? EMPTY_STRING} </ArcText.h6>
              <ArcText.caption fontSize={12}  whiteSpace='nowrap'> {drone.serialNumber ?? EMPTY_STRING} </ArcText.caption>
            </ArcStack>
            <ArcStack 
              spacing={1} 
              direction='row' 
              alignItems='center' 
              justifyContent='space-between'>
              <ArcText.button color={getDroneStatusDisplay(drone.status).color}> {getDroneStatusDisplay(drone.status).label} </ArcText.button>
              <ArcBatteryLife percent={drone.batteryHealth} light={true}/>
            </ArcStack>
          </ArcStack>
          
          <ArcButton.brand fullWidth onClick={onSelect}>
            Select
          </ArcButton.brand>
        </ArcStack>
        <ArcStack
          style={{
            position: "absolute",
            bottom: "-10px",
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "10px solid transparent",
            borderRight: "10px solid transparent",
            borderTop: "10px solid #fff",
          }}
        />
      </ArcStack>
    </OverlayView>
  );
};

